import { ClipLoader } from 'react-spinners';
import { AccentButton } from '../../../shared/Buttons';
import {
  ApplicationStep,
  CustomerWithDriverOnboarding,
  DropboxSignTemplateType,
  Entity,
} from '../../../types/apiTypes';
import { useScreenView } from '../../../utils/ScreenViewContext';
import { useState, useEffect, useRef, useCallback } from 'react';
// Import the HelloSign client library
import HelloSign from 'hellosign-embedded';
import useApi from '../../../utils/AxiosWrapper';
import { useUser } from '../../../utils/UserContext';
import toast from 'react-hot-toast';
import { Application } from '../../../types/dbTypes';

interface FinalConsentProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (step: string) => void;
  application_id: string;
  application: Application;
}

function FinalConsent({
  currStep,
  selectedRole,
  setStep,
  application_id,
  application,
}: FinalConsentProps) {
  const { screenView } = useScreenView();
  const [isSignatureComplete, setIsSignatureComplete] = useState(false);
  const { callProtectedApi } = useApi();
  const [signUrl, setSignUrl] = useState<string | null>(null);
  const { userRecord, userId } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Poll for final_consent_completed status after signing
  const pollFinalConsent = useCallback(async () => {
    if (!userId) return;
    setIsPolling(true);

    const poll = async () => {
      try {
        const response = await callProtectedApi(
          `/customers/${userId}/status/final_consent`,
          'get',
          {},
          'Error checking consent status.'
        );
        const finalConsent = response?.data?.data?.finalConsent;
        if (finalConsent) {
          // Consent confirmed on the backend
          if (pollingRef.current) clearInterval(pollingRef.current);
          setIsPolling(false);
          setStep(ApplicationStep.PendingReview);
        }
      } catch {
        // Silently retry on next interval
      }
    };

    // Check immediately, then poll every 3 seconds
    await poll();
    pollingRef.current = setInterval(poll, 3000);
  }, [userId, callProtectedApi, setStep]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Create HelloSign client and open embedded in container when signUrl is set.
  useEffect(() => {
    if (!signUrl || !containerRef.current) return;

    const helloSignClient = new HelloSign({
      clientId: process.env.REACT_APP_DROPBOX_SIGN_CLIENT_ID,
    });

    // Patch _sendMessage to handle null contentWindow by retrying.
    // hellosign-embedded@2.12.3 can hit a race where the iframe's
    // contentWindow is momentarily null during initialization.
    const originalSendMessage = (helloSignClient as any)._sendMessage;
    (helloSignClient as any)._sendMessage = function (msg: any) {
      const iframe = this._iFrameEl;
      if (!iframe?.contentWindow) {
        setTimeout(() => originalSendMessage.call(this, msg), 200);
        return;
      }
      originalSendMessage.call(this, msg);
    };

    helloSignClient.on('finish', async () => {
      try {
        setIsSignatureComplete(true);

        // Add URL parameter before transitioning
        const url = new URL(window.location.href);
        url.searchParams.set('consentCompleted', 'true');
        window.history.pushState({}, '', url);

        // Poll for backend to confirm final_consent_completed via webhook
        pollFinalConsent();
      } catch (error: any) {
        toast.error(
          'Error occurred in signature submission. Please try again. If error persists, please contact support: support@tachusa.com'
        );
      }
    });

    helloSignClient.on('close', () => {
      setSignUrl(null);
    });
    const isLocal = window.location.hostname === 'localhost';
    helloSignClient.open(signUrl, {
      container: containerRef.current,
      skipDomainVerification: isLocal,
      testMode: isLocal,
    } as any);

    return () => {
      helloSignClient.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signUrl]);

  const hasSignedBankAgreement = (userRecord as CustomerWithDriverOnboarding)
    ?.bank_agreement_accepted;
  const templateType = hasSignedBankAgreement
    ? DropboxSignTemplateType.CustomerAgreement
    : DropboxSignTemplateType.MembershipAgreement;

  const initiateSigningProcess = async () => {
    setIsLoading(true);
    try {
      const response = await callProtectedApi(
        `/documents/createSignatureRequest/${userId ?? ''}`,
        'post',
        {
          email: userRecord?.email,
          name: (userRecord as CustomerWithDriverOnboarding)?.business_name,
          role: 'carrier',
          metadata: {
            customer_id: userId,
            application_id: application_id,
            templateType: templateType,
          },
          templateType: templateType,
        }
      );

      const signUrlOut = await response?.data?.data?.sign_url;
      setSignUrl(signUrlOut);
    } catch (error: any) {
      console.error('Error initiating signing process:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (!isSignatureComplete && !isPolling) {
      return (
        <div className="relative flex h-full w-full flex-col items-center justify-start gap-y-[16px]">
          <h1 className="mb-2 justify-center text-center text-[32px] font-semibold text-primary">
            {hasSignedBankAgreement
              ? 'Sign Customer Agreement'
              : 'Sign Membership Agreement'}
          </h1>
          <h2 className="text-center text-[20px] text-graysecondary">
            {hasSignedBankAgreement
              ? 'Please review and sign the customer agreement to complete your application'
              : 'Please review and sign our membership agreement'}
          </h2>
          <AccentButton
            addClass="rounded-full w-[200px] h-[48px] space-x-2 flex flex-row items-center justify-center z-10"
            onClick={initiateSigningProcess}
          >
            <p>Sign Document</p>
            <ClipLoader
              color="#1D213B"
              loading={isLoading}
              size={40}
              aria-label="Loading Spinner"
              data-testid="loader"
              className="h-full w-full items-center justify-center"
            />
          </AccentButton>
        </div>
      );
    }

    return (
      <>
        <div className="flex w-full flex-col items-center justify-center gap-y-[16px]">
          <h1 className="justify-center text-center text-[32px] font-semibold text-primary">
            Processing your signature...
          </h1>
          <p>This may take a few moments</p>
          <ClipLoader
            color="#1D213B"
            loading={isPolling}
            size={60}
            aria-label="Loading Spinner"
            data-testid="loader"
            className="h-full w-full items-center justify-center"
          />
        </div>
      </>
    );
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div
            className={`flex h-full w-full flex-col items-center ${signUrl === null ? 'pt-[7rem]' : ''}`}
          >
            {renderContent()}
            <div
              ref={containerRef}
              id="signature-container"
              className={`absolute left-0 top-0 h-[100%] w-[100%] ${signUrl !== null ? 'z-40' : 'z-0'}`}
            />
          </div>
        );
      case 'tablet':
      case 'mobile':
        return (
          <div
            className={`flex h-full w-full flex-col items-center ${signUrl === null ? 'pt-[7rem]' : ''}`}
          >
            {renderContent()}
            <div
              ref={containerRef}
              id="signature-container"
              className={`absolute left-0 top-0 h-[100%] w-[100%] ${signUrl !== null ? 'z-40' : 'z-0'}`}
            />
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default FinalConsent;
